<template>
  <div data-pendo-ds="time_input">
    <InputLabel
      :info="info"
      :label="label"
      :required="required"
      :style="{ 'margin-bottom': 'var(--space-1)' }"
    />
    <div class="time-input-row">
      <SelectInput
        :id="`${id}-hours`"
        clear-text="Clear"
        :disabled="disabled"
        :dropdown-props="{ fixed, wrapperWidth: '67px' }"
        :error="error"
        :options="hourOptions"
        placeholder="HH"
        :value="hourValue"
        width="67px"
        @blur="$emit('blur')"
        @update:value="updateHour"
      />
      <SelectInput
        :id="`${id}-minutes`"
        :disabled="disabled"
        :dropdown-props="{ fixed, wrapperWidth: '67px' }"
        :error="error"
        :options="minuteOptions"
        placeholder="MM"
        required
        :value="minuteValue"
        width="67px"
        @blur="$emit('blur')"
        @update:value="updateMinute"
      />
      <SelectInput
        :id="`${id}-ampm`"
        :disabled="disabled"
        :dropdown-props="{ fixed, wrapperWidth: '67px' }"
        :error="error"
        :options="ampmOptions"
        required
        :value="ampmValue"
        width="67px"
        @blur="$emit('blur')"
        @update:value="updateAmPm"
      />
    </div>
    <slot name="errors" />
  </div>
</template>

<script>
import SelectInput from './SelectInput.vue'
import InputLabel from './InputLabel.vue'

export default {
  name: 'TimeInput',
  components: {
    SelectInput,
    InputLabel,
  },
  props: {
    value: {
      type: [String, null],
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    minuteIncrements: {
      type: Number,
      default: 10,
      validator: (value) => value > 0,
    },
    info: {
      type: String,
      default: '',
    },
    error: {
      type: Boolean,
      default: false,
    },
    fixed: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value', 'blur'],
  computed: {
    hourOptions() {
      const options = []

      for (let i = 1; i < 12; i++) {
        options.push({
          label: i,
          value: i,
        })
      }

      options.push({ label: 12, value: 0 })

      return options
    },
    minuteOptions() {
      const options = []

      for (let i = 0; i < 60; i += this.minuteIncrements) {
        options.push({
          label: i.toString().padStart(2, '0'),
          value: i,
        })
      }

      return options
    },
    ampmOptions() {
      return [
        { label: 'AM', value: 'AM' },
        { label: 'PM', value: 'PM' },
      ]
    },
    hourValue() {
      if (this.isEmpty) return ''

      return new Date(this.value).getUTCHours() % 12
    },
    minuteValue() {
      if (this.isEmpty) return ''

      return new Date(this.value).getMinutes()
    },
    ampmValue() {
      return new Date(this.value).getUTCHours() >= 12 ? 'PM' : 'AM'
    },
    isEmpty() {
      return this.value === '' || this.value === null
    },
  },
  methods: {
    updateHour(value) {
      if (value === '') {
        this.$emit('update:value', '')
        return
      }

      let date = new Date(this.value)
      if (this.isEmpty) {
        date = new Date()
        date.setUTCHours(0, 0, 0, 0)
      }

      if (this.ampmValue === 'PM') {
        date.setUTCHours(value + 12)
      } else {
        date.setUTCHours(value)
      }

      this.$emit('update:value', date.toISOString())
    },
    updateMinute(value) {
      let date = new Date(this.value)
      if (this.isEmpty) {
        date = new Date()
        date.setUTCHours(0, 0, 0, 0)
      }

      date.setMinutes(value)
      this.$emit('update:value', date.toISOString())
    },
    updateAmPm(value) {
      if (this.isEmpty) return

      const date = new Date(this.value)
      const hours = date.getHours()

      if (value === 'PM') {
        date.setHours(hours + 12)
      } else {
        date.setHours(hours - 12)
      }

      this.$emit('update:value', date.toISOString())
    },
  },
}
</script>

<style scoped lang="scss">
.time-input-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-3);

  > *:first-child {
    margin-right: var(--space-1);
    position: relative;

    &::after {
      content: ':';
      font-weight: var(--emphasis-high);
      position: absolute;
      top: 48%;
      right: -10.5px;
      transform: translateY(-50%);
    }
  }
}
</style>
